import type { DecodedField, DecodedLine } from "../api";
import { fieldLabel, formatAmount, formatFieldValue, isAmountField } from "../format";

// The delivery, read as a document rather than as characters: the envelope it
// came in, then one table per section of the records it carries.
//
// Built from the DECODED fields rather than from a per-delivery-type adapter, so
// every delivery — collections, payment advice, mandate information, mandate
// changes — is presented the same way, and a delivery type nobody has written a
// view for still shows its records.

// Columns every record carries for structure, not information. `pbsNumber` is
// constant within a section, so it is surfaced ONCE on the section header rather than
// repeated on every row — but it is never hidden: a delivery routinely carries a
// section per creditor, and without it two sections look interchangeable.
const STRUCTURAL = new Set([
  "systemIdentification",
  "dataRecordType",
  "pbsNumber",
  "dataRecordNumber",
  "transactionCode",
  "sectionNumber",
]);

interface Section {
  title: string;
  code?: string;
  /** The creditor this section was filed under — what distinguishes it from the next. */
  pbsNumber?: string;
  debtorGroupNumber?: string;
  records: DecodedLine[];
}

/** "Section start 0215 — Payments by payment slip" → "Payments by payment slip". */
function sectionTitle(line: DecodedLine): string {
  const label = line.label ?? "Section";
  const dash = label.indexOf("—");
  return dash === -1 ? label : label.slice(dash + 1).trim();
}

const fieldsOf = (line: DecodedLine): DecodedField[] => line.fields.filter((f) => !f.filler);

const valueOf = (line: DecodedLine, name: string): string | undefined =>
  line.fields.find((field) => field.name === name)?.value;

/** Splits a delivery into its sections, with the records each one carries. */
function sectionsOf(lines: DecodedLine[]): Section[] {
  const sections: Section[] = [];
  let current: Section | undefined;
  for (const line of lines) {
    if (line.recordType === "012") {
      current = {
        title: sectionTitle(line),
        code: line.code,
        pbsNumber: valueOf(line, "pbsNumber"),
        debtorGroupNumber: valueOf(line, "debtorGroupNumber"),
        records: [],
      };
      sections.push(current);
    } else if (line.recordType === "042" || line.recordType === "052" || line.recordType === "062") {
      if (!current) {
        current = { title: "Records", records: [] };
        sections.push(current);
      }
      current.records.push(line);
    } else if (line.recordType === "092") {
      current = undefined;
    }
  }
  return sections.filter((section) => section.records.length > 0);
}

function Facts({ line, only }: { line: DecodedLine; only: string[] }): JSX.Element {
  const fields = only
    .map((name) => line.fields.find((field) => field.name === name))
    .filter((field): field is DecodedField => field !== undefined && !field.filler);

  return (
    <div className="slip-facts">
      {fields.map((field) => (
        <div className="fact" key={field.name}>
          <span className="label">{fieldLabel(field.name)}</span>
          <span className="value">{formatFieldValue(field.name, field.value)}</span>
        </div>
      ))}
    </div>
  );
}

function RecordTable({ section }: { section: Section }): JSX.Element {
  // One column per field any record in the section carries, in layout order.
  const columns: string[] = [];
  for (const record of section.records) {
    for (const field of fieldsOf(record)) {
      if (!STRUCTURAL.has(field.name) && !columns.includes(field.name)) columns.push(field.name);
    }
  }

  const total = section.records.reduce((sum, record) => {
    const amount = record.fields.find((field) => isAmountField(field.name) && field.name === "amount");
    return sum + (amount?.numeric ?? 0);
  }, 0);

  return (
    <>
      <div className="section-title">
        <h3>{section.title}</h3>
        {section.code ? <span className="code-chip">{section.code}</span> : null}
        {section.pbsNumber ? (
          <span className="label">
            {fieldLabel("pbsNumber")} {section.pbsNumber}
            {section.debtorGroupNumber ? ` · ${fieldLabel("debtorGroupNumber")} ${section.debtorGroupNumber}` : ""}
          </span>
        ) : null}
        <span className="label">
          {section.records.length} record{section.records.length === 1 ? "" : "s"}
          {total > 0 ? ` · ${formatAmount(total)}` : ""}
        </span>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Record</th>
              {columns.map((column) => (
                <th key={column} className={isAmountField(column) ? "right" : undefined}>
                  {fieldLabel(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.records.map((record) => (
              <tr key={record.lineNumber}>
                <td>
                  <span className="chip" data-tone={toneOf(record.label)}>
                    {record.label ?? record.recordType}
                  </span>
                </td>
                {columns.map((column) => {
                  const value = valueOf(record, column);
                  return (
                    <td key={column} className={isAmountField(column) ? "right" : undefined}>
                      {value === undefined ? "" : formatFieldValue(column, value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Rejected, cancelled and charged-back records are the ones worth spotting. */
function toneOf(label: string | undefined): string {
  if (!label) return "quiet";
  return /rejected|cancelled|charged back|not notified|stop/i.test(label) ? "bad" : "";
}

export function Overview({ lines }: { lines: DecodedLine[] }): JSX.Element {
  const start = lines.find((line) => line.recordType === "002");
  const end = lines.find((line) => line.recordType === "992");
  const sections = sectionsOf(lines);

  return (
    <>
      {start ? (
        <>
          <div className="section-title">
            <h3>Delivery</h3>
            <span className="label">from the 002 and 992 records</span>
          </div>
          <Facts line={start} only={["deliveryIdentification", "dataSupplierNumber", "subsystem", "date"]} />
          {end ? (
            <Facts
              line={end}
              only={["numberOfSections", "numberOfRecordType042", "amount", "numberOfRecordType052And062"]}
            />
          ) : null}
        </>
      ) : null}

      {sections.map((section, index) => (
        <RecordTable key={`${section.title}-${index}`} section={section} />
      ))}
    </>
  );
}
